/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-09-07: Frame utility functions for Phase 4
 * Three-section split-screen support
 */

import { useMemo } from 'react';
import { App } from '@sage3/applications/schema';
import { useThrottleApps } from '../hooks';

/**
 * Hook to get all Frame apps on the current board.
 * Filters apps by type === 'Frame' and memoizes the result.
 */
export function useAvailableFrames() {
  const apps = useThrottleApps(250);

  return useMemo(() => {
    return apps.filter((app) => app.data.type === 'Frame');
  }, [apps]);
}

/**
 * Calculate viewport parameters to fit a Frame in a given section.
 * Returns position (center of frame) and scale to fit the frame in the section.
 *
 * @param frame - The Frame app to display
 * @param sectionWidth - Width of the section in pixels
 * @param sectionHeight - Height of the section in pixels
 * @returns Viewport parameters { x, y, scale }
 */
export function getFrameViewport(
  frame: App,
  sectionWidth: number,
  sectionHeight: number
): { x: number; y: number; scale: number } {
  // Frame has position (x, y) and size (w, h) in its data
  const frameX = frame.data.position.x;
  const frameY = frame.data.position.y;
  const frameWidth = frame.data.size.width;
  const frameHeight = frame.data.size.height;

  // Calculate scale to fit frame in section
  // Scale is the ratio of section size to frame size
  const scaleX = sectionWidth / frameWidth;
  const scaleY = sectionHeight / frameHeight;

  // Use the smaller scale to ensure entire frame fits
  // Apply 0.9 multiplier to add 10% padding around frame
  const scale = Math.min(scaleX, scaleY) * 0.9;

  // Calculate center of frame in board coordinates
  const frameCenterX = frameX + frameWidth / 2;
  const frameCenterY = frameY + frameHeight / 2;

  // Convert to camera-centric coordinates
  // Camera position is where the top-left of the viewport should be
  // To center the frame, we need: frameCenter - (viewportSize / 2) / scale
  const cameraX = frameCenterX - (sectionWidth / 2) / scale;
  const cameraY = frameCenterY - (sectionHeight / 2) / scale;

  // SAGE3 coordinate system: camera position is negated
  // To view board position (+X, +Y), camera must be at (-X, -Y)
  return {
    x: -cameraX,
    y: -cameraY,
    scale: scale,
  };
}

/**
 * Validate that a frame ID exists and is actually a Frame app.
 *
 * @param frameId - The frame ID to validate (can be null)
 * @param apps - Array of all apps
 * @returns true if frameId exists and is a Frame, false otherwise
 */
export function validateFrameExists(frameId: string | null, apps: App[]): boolean {
  if (!frameId) return false;

  const app = apps.find((a) => a._id === frameId);
  return app !== undefined && app.data.type === 'Frame';
}

/**
 * Get a Frame app by ID with type validation.
 *
 * @param frameId - The frame ID to find
 * @param apps - Array of all apps
 * @returns The Frame app if found and valid, undefined otherwise
 */
export function getFrameById(frameId: string | null, apps: App[]): App | undefined {
  if (!frameId) return undefined;

  const app = apps.find((a) => a._id === frameId);
  if (app && app.data.type === 'Frame') {
    return app;
  }

  return undefined;
}

/**
 * Check if both frames required for split mode are selected and valid.
 *
 * @param leftFrameId - Left frame ID
 * @param rightFrameId - Right frame ID
 * @param apps - Array of all apps
 * @returns true if both frames are valid, false otherwise
 */
export function canActivateSplitMode(
  leftFrameId: string | null,
  rightFrameId: string | null,
  apps: App[]
): boolean {
  return (
    validateFrameExists(leftFrameId, apps) &&
    validateFrameExists(rightFrameId, apps)
  );
}
